import { ChevronLeft, ChevronUp, CircleAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { StudentAvatar } from '@/components/shared/StudentAvatar/StudentAvatar';
import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup/RadioGroup';
import { useAuthStore } from '@/stores/authStore';
import type { Id } from '@/types/api';

import { toast } from '@/components/ui/Toast/Toast';
import { getSelectedDeliveryCenterSchedulingDetails } from '@/features/scheduling/utils/getSelectedDeliveryCenterSchedulingDetails';
import { isEnrollmentValid } from '@/features/scheduling/utils/isEnrollmentValid';
import { useTimeFor } from '@/hooks/useTimeFor';
import { getSchedulableDeliveryTypes, isEnrollmentSchedulable } from '@/features/scheduling/utils/schedulability';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import type { DeliveryType } from '@/types/scheduling';
import { cn } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { SessionDetailsRow } from '../../../SessionDetailsRow/SessionDetailsRow';
import type { StudentsSessionDetails } from '../../context/ScheduleFlowContext';
import { useScheduleFlow } from '../../context/useScheduleFlow';

const mapDeliveryTypeToKey = (deliveryType: 'IN-CENTER' | 'ONLINE') => {
  if (deliveryType === 'ONLINE') return 'online';
  return 'in_center';
};

export const SessionDetailsStep = () => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog.steps.sessionDetailsStep' });
  const { t: tUtils } = useTranslation('utils', { keyPrefix: 'sessionPackageNames' });
  const { t: tValidation } = useTranslation('utils', { keyPrefix: 'validation' });

  const { data, goToNextStep, goToPreviousStep, updateData } = useScheduleFlow();
  const [collapsedStudentsIds, setCollapsedStudentsIds] = useState<Set<Id>>(new Set());

  const { userData, studentsBgColors } = useAuthStore();
  const { timeZone: centerTimeZone } = useTimeFor('sessionTime');

  const userDataStudents = userData?.students ?? [];
  const configStudents = useMemo(() => data.config.data?.students ?? [], [data.config.data?.students]);

  const selectedStudentsConfigs = configStudents.filter((student) => data.selectedStudentsIds.includes(student.id));

  const handleNoOptions = useCallback(() => {
    const hasOneConfig = selectedStudentsConfigs.length === 1;

    if (!hasOneConfig) {
      return;
    }

    const onlyStudent = selectedStudentsConfigs[0];
    const validEnrollments = onlyStudent.enrollments.filter(
      (enrollment) => isEnrollmentValid(enrollment, centerTimeZone).valid && isEnrollmentSchedulable(enrollment, onlyStudent),
    );

    const hasOneEnrollment = validEnrollments.length === 1;

    const schedulableDeliveryTypes = hasOneEnrollment ? getSchedulableDeliveryTypes(validEnrollments[0], onlyStudent) : [];

    const hasOneDeliveryType = hasOneEnrollment && schedulableDeliveryTypes.length === 1;

    const hasOneDuration = hasOneEnrollment && validEnrollments[0].durationsInMinutes && validEnrollments[0].durationsInMinutes.length === 1;

    const shouldSkipSettings = hasOneConfig && hasOneEnrollment && hasOneDeliveryType && hasOneDuration;

    if (!shouldSkipSettings) {
      return;
    }

    if (shouldSkipSettings) {
      const onlyEnrollment = validEnrollments[0];
      const onlyDeliveryType = schedulableDeliveryTypes[0];
      const onlyDuration = onlyEnrollment.durationsInMinutes[0];

      const centerDetails = getSelectedDeliveryCenterSchedulingDetails(selectedStudentsConfigs, onlyStudent.id, mapDeliveryTypeToKey(onlyDeliveryType));

      if (!centerDetails) {
        throw new Error(`No center scheduling details found for student ${onlyStudent.id} with delivery type ${onlyDeliveryType}`);
      }

      updateData('studentSessionDetails', [
        {
          studentId: onlyStudent.id,
          enrollmentId: onlyEnrollment.id,
          deliveryType: mapDeliveryTypeToKey(onlyDeliveryType),
          durationInMinutes: onlyDuration,
          enrollmentType: onlyEnrollment.type,
          sessionType: onlyEnrollment.sessionType,
          enrollmentEndDate: onlyEnrollment.enrollmentEndDate,
          centerDetails: centerDetails,
        },
      ]);
      updateData('hasSessionDetailsSettings', false);

      void goToNextStep();
    }
  }, [selectedStudentsConfigs, updateData, goToNextStep, centerTimeZone]);

  const studentSessionDetailsSchema = z.object({
    studentId: z.number(),
    enrollmentId: z.number({ message: tValidation('optionsRequired') }),
    deliveryType: z.enum(['in_center', 'online'], { message: tValidation('optionsRequired') }),
    durationInMinutes: z.number({ message: tValidation('optionsRequired') }),
  });

  const formSchema = z.object({
    studentSessionDetails: z.array(studentSessionDetailsSchema).min(1),
  });

  useEffect(handleNoOptions, [handleNoOptions]);

  const getInitialStudentDetails = (): Partial<StudentsSessionDetails>[] => {
    return selectedStudentsConfigs.map((studentConfig) => {
      const existingData = data.studentSessionDetails?.find((detail) => detail.studentId === studentConfig.id);

      if (existingData) {
        return existingData;
      }

      if (studentConfig.enrollments.length === 0) {
        throw new Error(`No enrollments found for student ${studentConfig.id}`);
      }

      const studentValidEnrollments = studentConfig.enrollments.filter(
        (enrollment) => isEnrollmentValid(enrollment, centerTimeZone).valid && isEnrollmentSchedulable(enrollment, studentConfig),
      );

      if (studentValidEnrollments.length === 0) {
        return {};
      }

      const firstEnrollment = studentValidEnrollments[0];

      const firstEnrollmentSchedulableDeliveryTypes = getSchedulableDeliveryTypes(firstEnrollment, studentConfig);

      const deliveryType = mapDeliveryTypeToKey(firstEnrollmentSchedulableDeliveryTypes[0]);

      const centerDetails = getSelectedDeliveryCenterSchedulingDetails(selectedStudentsConfigs, studentConfig.id, deliveryType);

      if (!firstEnrollment) {
        throw new Error(`No enrollment found for student ${studentConfig.id}`);
      }

      if (!centerDetails) {
        throw new Error(`No center scheduling details found for student ${studentConfig.id} with delivery type ${deliveryType}`);
      }

      const isOnlyEnrollment = studentValidEnrollments.length === 1;
      const isOnlyDeliveryType = isOnlyEnrollment && firstEnrollmentSchedulableDeliveryTypes.length === 1;
      const isOnlyDuration = isOnlyEnrollment && firstEnrollment.durationsInMinutes?.length === 1;

      return {
        studentId: studentConfig.id,
        enrollmentId: isOnlyEnrollment ? firstEnrollment.id : undefined,
        enrollmentEndDate: isOnlyEnrollment ? firstEnrollment.enrollmentEndDate : undefined,
        enrollmentType: isOnlyEnrollment ? firstEnrollment.type : undefined,
        deliveryType: isOnlyDeliveryType ? deliveryType : undefined,
        durationInMinutes: isOnlyDuration ? firstEnrollment.durationsInMinutes?.[0] : undefined,
        sessionType: isOnlyEnrollment ? firstEnrollment.sessionType : undefined,
        centerDetails: centerDetails,
      };
    });
  };

  const getEnrollmentOptions = (studentId: Id, selectedEnrollmentId?: Id): { deliveryTypes: DeliveryType[]; durations: number[] } => {
    const studentData = selectedStudentsConfigs.find((student) => student.id === studentId);
    if (!studentData) return { deliveryTypes: [], durations: [] };

    if (!selectedEnrollmentId) {
      // Return options from first valid + schedulable enrollment if none selected
      const firstEnrollment = studentData.enrollments.find(
        (enrollment) => isEnrollmentValid(enrollment, centerTimeZone).valid && isEnrollmentSchedulable(enrollment, studentData),
      );

      return {
        deliveryTypes: firstEnrollment ? getSchedulableDeliveryTypes(firstEnrollment, studentData).map((el) => mapDeliveryTypeToKey(el)) : [],
        durations: firstEnrollment?.durationsInMinutes ?? [],
      };
    }

    const selectedEnrollment = studentData.enrollments.find((enrollment) => enrollment.id === selectedEnrollmentId);

    return {
      deliveryTypes: selectedEnrollment ? getSchedulableDeliveryTypes(selectedEnrollment, studentData).map((el) => mapDeliveryTypeToKey(el)) : [],
      durations: selectedEnrollment?.durationsInMinutes ?? [],
    };
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      studentSessionDetails: getInitialStudentDetails(),
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const mappedValues = values.studentSessionDetails.map((detail) => {
      const studentConfig = selectedStudentsConfigs.find((student) => student.id === detail.studentId);
      if (!studentConfig) {
        throw new Error(`Student config not found for student ID ${detail.studentId}`);
      }

      const centerDetails = getSelectedDeliveryCenterSchedulingDetails(selectedStudentsConfigs, detail.studentId, detail.deliveryType);

      if (!centerDetails?.center_id || !centerDetails.max_advance_booking_recurring || !centerDetails.max_advance_booking_single) {
        throw new Error(`No center scheduling details found for student ${detail.studentId} with delivery type ${detail.deliveryType}`);
      }

      const enrollmentDetails = studentConfig.enrollments.find((enrollment) => enrollment.id === detail.enrollmentId);

      if (!enrollmentDetails?.enrollmentEndDate || !enrollmentDetails.sessionType) {
        throw new Error(`No session type found for student ${detail.studentId} with enrollment ID ${detail.enrollmentId}`);
      }

      return {
        ...detail,
        enrollmentType: enrollmentDetails.type,
        sessionType: enrollmentDetails.sessionType,
        enrollmentEndDate: enrollmentDetails.enrollmentEndDate,
        centerDetails: centerDetails,
      };
    });

    void captureAnalyticsEvent('scheduling_sessiondetails_selected', { studentSessionDetails: mappedValues });
    updateData('studentSessionDetails', mappedValues);
    goToNextStep();
  };

  const toggleStudentCollapse = (studentId: number) => {
    setCollapsedStudentsIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  return (
    <>
      <DialogBody>
        <div className='mb-6'>
          <h3 className='text-heading-m-bold text-text-default-primary mb-2'>{t('title')}</h3>
          <p className='text-body-m-regular text-text-default-secondary'>{t('subheading')}</p>
        </div>
        <Form {...form}>
          <form
            id='session-details-form'
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit, () => {
                toast({
                  variant: 'destructive',
                  description: t('missingInfoError'),
                  showClose: true,
                });
              })(e);
            }}
            className='flex flex-1 flex-col'
          >
            <div className='flex flex-col gap-6'>
              {selectedStudentsConfigs.map((studentData, index) => {
                const isCollapsed = collapsedStudentsIds.has(studentData.id);

                const studentFromUserData = userDataStudents.find((s) => s.id === studentData.id);

                if (!studentFromUserData) {
                  throw new Error(`Configuration not found for student ${studentData.id}`);
                }

                const sectionId = `student-${studentData.id}-section`;
                const headerId = `student-${studentData.id}-header`;

                const currentEnrollmentId = form.watch(`studentSessionDetails.${index}.enrollmentId`);
                const enrollmentOptions = getEnrollmentOptions(studentData.id, currentEnrollmentId);

                const enrollmentsWithValidity = studentData.enrollments.map((enrollment) => ({
                  enrollment,
                  isValid: isEnrollmentValid(enrollment, centerTimeZone).valid && isEnrollmentSchedulable(enrollment, studentData),
                }));

                const validEnrollments = enrollmentsWithValidity.filter(({ isValid }) => isValid).map(({ enrollment }) => enrollment);

                const studentHasErrors = form.formState.errors.studentSessionDetails?.[index];

                return (
                  <div key={studentData.id} id={sectionId} className='border-outline-neutral flex flex-col gap-6 overflow-hidden rounded-lg border p-5'>
                    <div className='flex items-center justify-between gap-4'>
                      <div className='flex items-center gap-2.5 overflow-hidden'>
                        <StudentAvatar
                          student={{
                            ...studentFromUserData,
                            bgColor: studentsBgColors.get(studentFromUserData.id) ?? '#eee',
                          }}
                        />
                        <p className='max-w-[80%] overflow-hidden text-ellipsis' id={headerId}>
                          {studentFromUserData.firstName} {studentFromUserData.lastName}
                        </p>
                      </div>
                      <div className='flex items-center'>
                        {studentHasErrors && <CircleAlert className='text-text-error-primary size-6' />}
                        <Button
                          type='button'
                          variant='icon'
                          size='icon-l'
                          onClick={() => toggleStudentCollapse(studentFromUserData.id)}
                          className='flex items-center justify-center px-2.5'
                          aria-expanded={!isCollapsed}
                          aria-controls={`${sectionId}-content`}
                          aria-label={
                            isCollapsed
                              ? t('expandRowAriaLabel', { firstName: studentFromUserData.firstName, lastName: studentFromUserData.lastName })
                              : t('collapseRowAriaLabel', { firstName: studentFromUserData.firstName, lastName: studentFromUserData.lastName })
                          }
                        >
                          <ChevronUp
                            className={cn('size-6', {
                              'rotate-180': isCollapsed,
                            })}
                          />
                        </Button>
                      </div>
                    </div>

                    {!isCollapsed && (
                      <div className='flex flex-col gap-6' id={`${sectionId}-content`} aria-labelledby={headerId}>
                        <FormField
                          control={form.control}
                          name={`studentSessionDetails.${index}.enrollmentId`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className='text-text-default-secondary'>{t('enrollmentTypeLabel')}</FormLabel>
                              <FormMessage className='-mb-1' />
                              <FormControl>
                                <RadioGroup
                                  className='mt-3'
                                  onValueChange={(value) => {
                                    const enrollmentId = Number.parseInt(value, 10);
                                    field.onChange(enrollmentId);

                                    // Reset dependent fields when enrollment changes
                                    const newOptions = getEnrollmentOptions(studentData.id, enrollmentId);
                                    if (!newOptions?.deliveryTypes || !newOptions?.durations) return;

                                    const isSingleDeliveryType = newOptions.deliveryTypes.length === 1;
                                    const isSingleDuration = newOptions.durations.length === 1;

                                    if (isSingleDeliveryType) {
                                      form.setValue(`studentSessionDetails.${index}.deliveryType`, newOptions.deliveryTypes[0]);
                                      form.clearErrors(`studentSessionDetails.${index}.deliveryType`);
                                    } else {
                                      form.resetField(`studentSessionDetails.${index}.deliveryType`);
                                    }
                                    if (isSingleDuration) {
                                      form.setValue(`studentSessionDetails.${index}.durationInMinutes`, newOptions.durations[0]);
                                      form.clearErrors(`studentSessionDetails.${index}.durationInMinutes`);
                                    } else {
                                      form.resetField(`studentSessionDetails.${index}.durationInMinutes`);
                                    }
                                  }}
                                  value={field.value?.toString() || ''}
                                >
                                  {validEnrollments.map((enrollment) => (
                                    <div
                                      key={enrollment.id}
                                      className='bg-fill-background-light mb-1 flex flex-row items-center justify-between rounded-lg px-5 py-3'
                                    >
                                      <label className='text-body-m-semibold flex-1' htmlFor={`enrollment-${enrollment.id}`}>
                                        {enrollment.name || tUtils(enrollment.type)}
                                      </label>
                                      {validEnrollments.length > 1 && <RadioGroupItem id={`enrollment-${enrollment.id}`} value={enrollment.id.toString()} />}
                                    </div>
                                  ))}
                                </RadioGroup>
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {currentEnrollmentId && (
                          <FormField
                            control={form.control}
                            name={`studentSessionDetails.${index}.deliveryType`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('sessionTypeLabel')}</FormLabel>
                                <FormMessage className='-mb-1' />
                                <FormControl>
                                  <RadioGroup className='mt-3' onValueChange={field.onChange} value={field.value ?? ''}>
                                    {enrollmentOptions.deliveryTypes?.map((deliveryType) => (
                                      <SessionDetailsRow
                                        key={deliveryType}
                                        value={deliveryType ?? ''}
                                        label={t(`sessionTypeOptions.${deliveryType}`)}
                                        hideRadio={enrollmentOptions.deliveryTypes.length < 2}
                                      />
                                    ))}
                                  </RadioGroup>
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        )}

                        {currentEnrollmentId && (
                          <FormField
                            control={form.control}
                            name={`studentSessionDetails.${index}.durationInMinutes`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('durationLabel')}</FormLabel>
                                <FormMessage className='-mb-1' />
                                <FormControl>
                                  <RadioGroup
                                    className='mt-3'
                                    onValueChange={(value) => field.onChange(Number.parseInt(value, 10))}
                                    value={field.value?.toString() ?? ''}
                                  >
                                    {enrollmentOptions.durations.map((duration) => (
                                      <SessionDetailsRow
                                        key={duration}
                                        value={duration.toString() ?? ''}
                                        label={t('minutes', { value: duration })}
                                        hideRadio={enrollmentOptions.durations.length < 2}
                                      />
                                    ))}
                                  </RadioGroup>
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </form>
        </Form>
      </DialogBody>

      <DialogFooter>
        {configStudents.length > 1 && (
          <Button type='button' variant='text-secondary' onClick={goToPreviousStep} className='flex-1'>
            <ChevronLeft className='text-text-default-secondary size-5' />
            <span>{t('backButton.label')}</span>
          </Button>
        )}
        <Button type='submit' form='session-details-form' className='flex-1'>
          {t('continueButton.label')}
        </Button>
      </DialogFooter>
    </>
  );
};
